import React from 'react';
import { useMediaQuery } from 'react-responsive';
import Div100vh from 'react-div-100vh';
import classnames from 'classnames';
import { Dialog, DialogCloseReason } from '@guildeducationinc/recess';
import { MOBILE_QUERY } from '@src/constants';
import { useModal } from '@src/context/ModalContext';
import { clickable } from '@src/analytics';
import './BaseModal.scss';

// true mobile browsers cover the bottom of the viewport with 100vh containers
// this package aims to mitigate the issue:
// https://www.npmjs.com/package/react-div-100vh

/**
 * App-level container to be populated with modal content as determined by the ModalContext.
 * @packageDocumentation
 */

const BaseModal: React.FC = () => {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const { closeModal, isModalOpen, modalContent, modalOptions } = useModal();
  const {
    closable,
    containerClass,
    onModalClose,
    tag,
    title,
    titleComponent,
    titleSize,
    labelledBy,
    describedBy,
    primaryButton,
    secondaryButton,
  } = modalOptions;

  const handleClose = (reason: DialogCloseReason) => {
    // don't close the overlay window when the background is clicked
    if (reason !== 'backdropClick') {
      if (onModalClose) {
        onModalClose();
      }

      if (tag) {
        clickable({
          loggedIn: true,
          product: 'home',
          tag,
          text: 'close',
          type: 'action icon',
        });
      }
      closeModal();
    }
  };

  const dialogClassNames = classnames('dialog', containerClass, {
    noClose: !closable,
  });

  return (
    <Dialog
      isOpen={isModalOpen}
      onClose={handleClose}
      fullScreen={isMobile}
      className={dialogClassNames}
      title={title}
      titleComponent={titleComponent}
      titleSize={titleSize}
      labelledBy={labelledBy}
      describedBy={describedBy}
      buttonA={secondaryButton}
      buttonB={primaryButton}
    >
      {isMobile ? <Div100vh>{modalContent}</Div100vh> : <>{modalContent}</>}
    </Dialog>
  );
};

export default BaseModal;
